import React, { useRef, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Picker from 'coloreact';
import classNames from 'classnames';

import { editContainer } from '../../modules/editor';
import { RootState } from '../../modules';
import { RGBToHex } from '../../util/color';
import { isHexCode } from '../../util/validator';

import useAlert from '../../hooks/useAlert';
import useDebounce from '../../hooks/useDebounce';
import useOnClickOutside from  '../../hooks/useOnClickOutside';

import deleteIcon from '../../assets/images/icon/delete.png';
import resetIcon from '../../assets/images/icon/color_reset.png';


type Props = {
  className?: string;
	value: string;
  position?: 'left' | 'right';
  showResetValue?: boolean;
  onChangeValue: (value:string) => void;
  onResetValue: () => void;
  onClose: () => void;
  leftPosition?: number;
};

const MAX_PALLETTE_COUNT = 11;

function ColorPickerPanel({ className, value, position, onChangeValue, onClose, onResetValue, showResetValue, leftPosition }: Props) {
  const dispatch = useDispatch();
  const Alert = useAlert();

  const [oldValue, setOldValue] = useState(value);
	const [inputValue, setInputValue] = useState(value);
  const [dbColorValue, colorValue, setColorValue] = useDebounce<string>(value, 200);
  const [verticalPosition, setVerticalPosition] = useState('top');
  
  const pallette = useSelector((state: RootState) => state.editor.container.pallette);  
	const container = useSelector((state: RootState) => state.editor.container);

  const ref = useRef(null);
	useOnClickOutside(ref, ():any => {
		cancel();
  });

  useEffect(() => {
    setColorValue(RGBToHex(value));
    setInputValue(RGBToHex(value).toUpperCase());
  }, [])

  useEffect(() => {
		if (colorValue === undefined || colorValue.length === 0) {
			return;
		}
		if (colorValue === value) {
			return;
    }
    onChangeValue(dbColorValue);
  }, [dbColorValue])
  
  useEffect(() => {
    setVerticalPosition((window.innerHeight - 340) <= ref?.current?.getBoundingClientRect().y ? 'bottom' : 'top');
  }, [])

  const onChange = (color:any) => {
		setColorValue(color.hexString);
	}

	const onChangeByPallette = (color:string) => {
		setColorValue(RGBToHex(color));
    setInputValue(RGBToHex(color));
	}

  const resetValue = () => {		
    onResetValue();
    setColorValue('');
    setInputValue('');
  }
	
	const onChangeByInput = (e:any) => {
		const {target:{value}, key} = e;
    
		setInputValue(RGBToHex(value));
		if (key === 'Enter' || (value[0] != '#' && value.length === 6) || value.length === 7) {
      let hashedValue = value;
      if (value[0] !== '#') {
        hashedValue = '#' + value;
      }
			if (isHexCode(hashedValue)) {
        setColorValue(hashedValue.toUpperCase())
			} else {
				Alert.error(Alert?.msg?.CHECK_HEX_CODE);
			}
		}
	}

	const savePallette = (color:string) => {
		if (!pallette.includes(color)) {
			let newPallette:string[] = pallette.split(';');
			newPallette.unshift(color);
			newPallette = newPallette.slice(0, MAX_PALLETTE_COUNT);
			
			let newContainer = {
				...container,
				pallette: newPallette.join(';')
			}
			dispatch(editContainer(newContainer));
		}
  }
  
  const deleteChip = (item:string) => {
		Alert.confirm(Alert?.msg?.REMOVE_PALLETTE_ITEM, () => {
			let newPallette: string[] = pallette.split(';');
			let newPalletteFiltered = newPallette.filter((el) => el !== item);
	
			let newContainer = {
				...container,
				pallette: newPalletteFiltered.join(';')
			}
			dispatch(editContainer(newContainer));
		});
	}
  
  const confirm = () => {
		savePallette(value);
		setOldValue(value);
    onChangeValue(colorValue);
		onClose();
	}

	const cancel = () => {    
    setColorValue(oldValue);
    onChangeValue(oldValue);
		onClose();
  }  

  return (
    <>
      <div ref={ref} className={classNames('color-picker-panel', position, verticalPosition, className)} style={{left: leftPosition ? `${leftPosition + 345}px` : ''}}>
        <Picker
          style={{
            position: "relative",
            height: "200px",
            width: "200px",
            paddingLeft: "1.3em"
          }}
          opacity={false}
          color={colorValue}
          onChange={onChange}
        />
        <div className="input">
          <span className="value" style={{ background: colorValue}} >
          </span>
          <input type="text" value={inputValue} onChange={onChangeByInput} />
        </div>
        <div className="pallette">
          <ul className="pallette-list">
            {
              showResetValue && (
              <li>
                <button 
                  className="chip reset"
                  style={{ background: '', color: '#fff', fontSize: '20px', lineHeight:'20px' }}
                  onClick={resetValue}
                >	
                  <img src={resetIcon} alt="reset"/>
                </button>
              </li>
              )
            }
            {
              pallette?.split(';').filter((item:string) => item !== '').map((item:string, index:number) => (
                <li key={index}>
                  <button 
                    className="chip"
                    style={{background: item}}
                    onClick={() => { onChangeByPallette(item)}}
                  >
                  </button>
                  <button 
                    className="delete"
                    onClick={() => { deleteChip(item)}}
                  >
                    <img src={deleteIcon} alt="delete" width="10" />
                  </button>
                </li>
              ))
            }
          </ul>
        </div>
        <div className="btns">
          <button className="btn small" onClick={confirm}>적용하기</button>
          {/* <button className="btn small" onClick={cancel}>취소</button> */}
        </div>
      </div>
    </>
  );
}

ColorPickerPanel.defaultProps = {
  showResetValue: true,
  position: 'left'
};

export default ColorPickerPanel;